import { AxiosRequestConfig, CancelTokenSource, Method } from "axios";
import { axiosInstance, routeParamsFiller } from "@/plugins/axios";
import { plainToClass } from "class-transformer";
import { validate, ValidationError } from "class-validator";
import * as ContentDisposition from "content-disposition";
import { ErrorHiddenFromConsole } from "@/models/ErrorHiddenFromConsole";

// eslint-disable-next-line
export function customJsonSerializer(param: any): string {
  return "json=" + encodeURIComponent(JSON.stringify(param));
}

export default class CancellableHelper<T> {
  cancellableToken: CancelTokenSource | null = null;
  url: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _type?: any;
  nullable!: boolean;
  // eslint-disable-next-line
  jsonSerializer = false;
  allowedStatuses = [200, 204];
  requestConfig: AxiosRequestConfig = {};
  isFile = false;
  headers: Record<string, string> = {};

  // eslint-disable-next-line
  constructor(url: string, _type?: any, nullable: boolean = false) {
    this.url = url;
    this._type = _type;
    this.nullable = nullable;
  }

  public async request(params: unknown = {}, method: Method, data: unknown | null = null): Promise<T> {
    if (this.cancellableToken !== null) {
      this.cancellableToken.cancel("Next request started");
    }
    this.cancellableToken = axiosInstance.CancelToken.source();
    const url = routeParamsFiller(params, this.url);
    if (data !== null) {
      routeParamsFiller(data, url);
    }
    if (this.isFile) {
      this.requestConfig.responseType = "blob";
      this.requestConfig.headers;
    }

    const response = await axiosInstance.request({
      ...{
        url: this.checkURL(url),
        method: method,
        params: params,
        data: data === null ? params : data,
        cancelToken: this.cancellableToken.token,
        headers: { ...this.headers },
        paramsSerializer: this.jsonSerializer ? customJsonSerializer : axiosInstance.defaults.paramsSerializer,
      },
      ...this.requestConfig,
    });
    // this.cancellableToken = null;
    if (!this.allowedStatuses.includes(response.status)) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (typeof response.message !== "undefined" && response.message === "Next request started") {
        throw new ErrorHiddenFromConsole(`Next request started`);
      }
      throw new Error(`Unexpected response status code: ${response.status} - ${response.statusText}`);
    }

    if (response.data === "" || response.data === undefined) {
      response.data = null;
    }
    if (this._type !== undefined) {
      let responseData = null;
      if (Array.isArray(response.data)) {
        responseData = [];
        for (const item of response.data) {
          const itemData = await this.plainToClassWithErrorReport(item);
          responseData.push(itemData);
        }
      } else if (response.data !== null) {
        responseData = await this.plainToClassWithErrorReport(response.data);
      } else if (!this.nullable) {
        this.reportTypeMismatch(null, []);
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return responseData as T;
    }
    if (response.data instanceof Blob && typeof response.headers["content-disposition"] !== "undefined") {
      const filename = ContentDisposition.parse(response.headers["content-disposition"]).parameters.filename;
      return new File([response.data], filename, {
        type: response.data.type,
      }) as unknown as T;
    } else if (
      response.headers["content-type"] !== undefined &&
      ["application/pdf", "application/zip"].includes(response.headers["content-type"])
    ) {
      return new Blob([response.data], {
        type: response.headers["content-type"],
      }) as unknown as T;
    }
    return response.data as T;
  }

  public async get(params: unknown = {}): Promise<T> {
    return this.request(params, "GET");
  }

  public async post(params: unknown = {}, data: unknown | null = null): Promise<T> {
    return this.request(params, "POST", data);
  }

  public addHeader(key: string, value: string) {
    this.headers[key] = value;
  }

  // eslint-disable-next-line
  private async plainToClassWithErrorReport(item: any): Promise<any> {
    if (typeof this._type === "string") {
      if (this.nullable && item === null) return null;
      if (typeof item === this._type) return item;
      this.reportTypeMismatch(item, [{ property: "should be " + this._type }]);
      throw new Error("Unexpected result type");
    } else {
      const itemData = plainToClass(this._type, item, {
        exposeDefaultValues: true,
        excludeExtraneousValues: true,
      });
      const typeValidationErrors: Array<ValidationError> = await CancellableHelper.checkTypes(itemData);
      if (typeValidationErrors.length > 0) {
        this.reportTypeMismatch(itemData, typeValidationErrors);
      }
      return itemData;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static async checkTypes(response: any): Promise<Array<ValidationError>> {
    return await validate(response);
  }

  private reportTypeMismatch(response: unknown, validationErrors: Array<ValidationError>): void {
    const env = process.env.NODE_ENV;
    if (env === "production") {
      this.reportTypeMismatchProdEnv(response, validationErrors);
    } else {
      this.reportTypeMismatchDevEnv(response, validationErrors);
    }
  }

  private reportTypeMismatchDevEnv(response: unknown, validationErrors: Array<ValidationError>): void {
    alert("Wrong response type detected, check console!");
    console.error("Response doesn't  match declared type");
    console.error("URL: " + this.url);
    console.error("Result:");
    console.error(JSON.stringify(response));
    console.error(response);
    console.error(JSON.stringify(validationErrors));
    console.error(validationErrors);
  }

  private reportTypeMismatchProdEnv(response: unknown, validationErrors: Array<ValidationError>): void {
    console.warn("Response doesn't  match declared type");
    console.warn("URL: " + this.url);
    console.warn("Result:");
    console.warn(JSON.stringify(response));
    console.warn(JSON.stringify(validationErrors));
  }

  public static openFile(file: Blob): void {
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(file);
    link.target = "_blank";
    link.click();
  }

  public static downloadFile(file: Blob, name: string): void {
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(file);
    link.setAttribute("download", name);
    link.target = "_blank";
    link.click();
  }

  private checkURL(url: string): string {
    if (url.charAt(0) == "/") return url;
    console.error("Wrong URL: missing '/'", url);
    return "/" + url;
  }
}
